import type { TrafficObservation } from '../types'
import { envList, ENV_LABEL, type EnvSet } from './env'

/* ============================================================================
 * МОДЕЛЬ ЦЕЛИ ДЛЯ ИНДИКАТОРА ВОЗДУШНОЙ ОБСТАНОВКИ
 * Единая модель для таблицы целей, инструмента «Дальность и азимут» и карты.
 * ========================================================================= */

export type TrackKind = 'cooperative' | 'noncooperative' | 'intruder'

export interface TrafficTrack {
  id: string
  callsign: string
  kind: TrackKind
  /** Среда наблюдения: ['air'] | ['water'] | ['air','water'] (гибрид). */
  env: EnvSet
  /** Подпись среды для UI: «Воздух», «Вода», «Воздух + Вода». */
  envLabel: string
  /** true, если аппарат принадлежит нескольким средам сразу. */
  envHybrid: boolean
  /** Источник: ADS-B, Remote ID (ЭРА-ГЛОНАСС), РЛС, RF */
  source: string
  /** Идентификатор ИСТОЧНИКА данных (сессия FB / провайдер) — задача №7 */
  providerId: string | null
  /** Тип источника: flight_blender | sppi | erra_glonass | radar | rf */
  providerKind: string | null
  lat: number | null
  lon: number | null
  /** Высота над MSL (ASL), м — задача №9 */
  altM: number | null
  /**
   * Высота над рельефом (AGL), м. null, когда рельеф недоступен:
   * «нет данных» и «0 м над землёй» — разные вещи, и путать их нельзя.
   */
  aglM: number | null
  /** Высота рельефа (REL), м над MSL */
  relM: number | null
  /** Оценка точности REL из DEM, м. Показывается рядом с AGL. */
  terrainUncertaintyM: number | null
  /** Разрешение растра рельефа, м/пиксель */
  terrainResolutionM: number | null
  /** Скорость, м/с */
  speedMs: number | null
  /** Курс, градусы истинного севера */
  headingDeg: number | null
  observedAt: string | null
  raw: TrafficObservation
}

const num = (v: unknown): number | null => {
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/**
 * Признак нарушителя: цель без позывного и без ICAO, при этом не кооперативная.
 * Такая цель не подаёт сигнатуру Remote ID и требует идентификации.
 */
export function classify(obs: TrafficObservation): TrackKind {
  const hasId = Boolean(obs.icao || obs.callsign)
  if (obs.cooperative === false && !hasId) return 'intruder'
  if (obs.cooperative === false) return 'noncooperative'
  return 'cooperative'
}

/**
 * Человекочитаемое имя источника трафика по ASTM F3411 / ICAO.
 *
 * ВАЖНО (честность данных): приходит число из enum, а не строка. Неизвестное
 * значение НЕ угадывается — возвращается помеченное «неизвестный код N», чтобы
 * в таблице не появилось правдоподобная, но выдуманная подпись.
 */
const TRAFFIC_SOURCE_LABEL: Record<number, string> = {
  0: 'ADS-B',
  1: 'ADS-B ретранслятор',
  2: 'FLARM',
  3: 'Сетевой источник',
  4: 'OSTrich',
}

export function trafficSourceLabel(v: unknown): string | null {
  const n = Number(v)
  if (!Number.isInteger(n) || v === null || v === undefined) return null
  return TRAFFIC_SOURCE_LABEL[n] ?? `источник Трафик #${n}`
}

/** Приведение наблюдения к модели цели */
export function toTrack(obs: TrafficObservation, index: number): TrafficTrack {
  const kind = classify(obs)
  const source =
    obs.source ?? (kind === 'cooperative' ? 'ADS-B' : kind === 'intruder' ? 'РЛС/RF' : 'РЛС')
  const tSource = trafficSourceLabel(obs.traffic_source)
  const envs = envList(obs.env)
  return {
    id: String(obs.id ?? obs.icao ?? obs.callsign ?? `track-${index}`),
    callsign: String(obs.callsign ?? obs.icao ?? 'БЕЗ ОПОЗНАНИЯ'),
    kind,
    env: envs,
    envLabel: envs.map((e) => ENV_LABEL[e]).join(' + '),
    envHybrid: envs.length > 1,
    source: tSource ?? source,
    providerId: obs.provider_id ? String(obs.provider_id) : null,
    providerKind: obs.provider_kind ? String(obs.provider_kind) : null,
    lat: num(obs.lat),
    lon: num(obs.lon),
    // alt_m — это то, что пришло из FB (ASL). Значения высот от DEM приходят
    // отдельными полями (asl_m/agl_m/rel_m), и они важнее: AGL без них —
    // это догадка, а не измерение.
    altM: num(obs.asl_m ?? obs.alt_m),
    aglM: num(obs.agl_m),
    relM: num(obs.rel_m),
    terrainUncertaintyM: num(obs.terrain_uncertainty_m),
    terrainResolutionM: num(obs.terrain_resolution_m),
    speedMs: num(obs.speed_ms),
    headingDeg: num(obs.heading_deg),
    observedAt: obs.observed_at ?? null,
    raw: obs,
  }
}

/** Преобразование массива наблюдений в список целей */
export function toTracks(list: TrafficObservation[]): TrafficTrack[] {
  return list.map(toTrack)
}
